/**
 * Pure formatting helpers for the Cost Analysis dashboard.
 *
 * Money values come from the backend as `decimal.Decimal`-shaped strings
 * (e.g. "412.385000" or "-12.340000"); display formatting is the FE's
 * job. Backend pre-rounds to six decimals — we just collapse to two for
 * presentation and respect the sign.
 */

const CURRENCY_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FMT = new Intl.NumberFormat("en-US");

const PCT_FMT = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Format a Decimal-string or number as US currency.
 *
 * Negative values render as "-$12.34" — `Intl.NumberFormat`'s default
 * behavior for USD is `($12.34)` accounting style depending on locale,
 * which is wrong for a dashboard. We normalize to a leading minus so
 * negative-overhead-due-to-credits renders unambiguously and can be
 * styled in the same neutral color as positive values.
 *
 * Returns "—" for null/undefined/NaN to keep the table layout stable.
 */
export const formatCurrency = (raw) => {
  if (raw == null || raw === "") return "—";
  const n = typeof raw === "string" ? parseFloat(raw) : raw;
  if (!Number.isFinite(n)) return "—";
  if (n < 0) return `-${CURRENCY_FMT.format(Math.abs(n))}`;
  return CURRENCY_FMT.format(n);
};

/**
 * Format an optional product-cost field, treating missing/invalid data as zero.
 *
 * The product-cost fields were added after the original Cost Analysis API.
 * Rendering old-backend payloads as "$0.00" keeps the dashboard usable during
 * a staggered frontend/backend rollout without changing the accounting fields'
 * existing em-dash semantics.
 */
export const formatCurrencyOrZero = (raw) => {
  if (raw == null || raw === "") return CURRENCY_FMT.format(0);
  const n = typeof raw === "string" ? parseFloat(raw) : raw;
  if (!Number.isFinite(n)) return CURRENCY_FMT.format(0);
  return formatCurrency(n);
};

/**
 * Format an integer count (tokens, calls, records) with thousands separators.
 */
export const formatCount = (n) => {
  if (n == null) return "—";
  const v = typeof n === "string" ? parseFloat(n) : n;
  if (!Number.isFinite(v)) return "—";
  return NUMBER_FMT.format(Math.round(v));
};

/**
 * Format a 0..1 ratio as a percentage with one decimal ("98.7%").
 *
 * `parseFloat` on a Decimal-string ratio keeps enough precision through
 * the Intl formatter — we don't need explicit Decimal math for display.
 */
export const formatPercent = (raw) => {
  if (raw == null || raw === "") return "—";
  const n = typeof raw === "string" ? parseFloat(raw) : raw;
  if (!Number.isFinite(n)) return "—";
  return PCT_FMT.format(n);
};

/**
 * Compute "tokens / total tokens" ratio to a percent string.
 *
 * Used by the matter-detail "How is this calculated?" card to show the
 * matter's share of overall LLM activity that drives its allocated
 * overhead slice.
 */
export const ratio = (numerator, denominator) => {
  const n = typeof numerator === "string" ? parseFloat(numerator) : numerator;
  const d =
    typeof denominator === "string" ? parseFloat(denominator) : denominator;
  if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return 0;
  return n / d;
};

/**
 * Compute a safe 0..1 share for a stage cost within total pipeline cost.
 *
 * A zero/negative denominator or stage value cannot produce a meaningful
 * product share, so it renders as 0% instead of NaN, Infinity, or a negative
 * percentage. Clamp inconsistent payloads at 100% to keep the compact table
 * readable while the raw dollar values remain visible for diagnosis.
 */
export const costShare = (value, total) => {
  const v = typeof value === "string" ? parseFloat(value) : value;
  const t = typeof total === "string" ? parseFloat(total) : total;
  if (!Number.isFinite(v) || !Number.isFinite(t) || v <= 0 || t <= 0) return 0;
  return Math.min(1, v / t);
};

/**
 * Format an ISO date string (YYYY-MM-DD or full ISO) as a short
 * human-readable date.
 *
 * Backend dates are calendar dates representing UTC midnight (window
 * boundaries) or YYYY-MM-DD calendar days (by_day rows). `new Date(iso)`
 * parses both as UTC midnight; calling toLocaleDateString without a
 * timeZone forces local-tz coercion, which rolls back to "yesterday" in
 * any negative-UTC timezone (the firm operates in US time → off-by-one
 * everywhere). Pin timeZone: "UTC" so the displayed calendar date
 * matches the backend's calendar date exactly. Bugbot R2 catch.
 */
export const formatDate = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Compute the bar-width percentage (0..100) for a horizontal bar chart.
 *
 * Used in the by_model / by_operation tables — the largest row gets
 * 100% width and the rest scale relative to it. Returns 0 when the
 * denominator can't be computed (avoids `Infinity%` widths).
 */
export const barWidthPct = (value, max) => {
  const v = typeof value === "string" ? parseFloat(value) : value;
  const m = typeof max === "string" ? parseFloat(max) : max;
  if (!Number.isFinite(v) || !Number.isFinite(m) || m <= 0) return 0;
  return Math.max(0, Math.min(100, (Math.abs(v) / m) * 100));
};
